import React from 'react';

export const isMacLike = () => {
  // Check if the code is running in a browser environment
  if (typeof navigator !== 'undefined') {
    return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
  }
  return false; // Default to false if navigator is not available
};

export const macPC = (mac: string, pc: string) => (isMacLike() ? mac : pc);

type Options = {
  metaWindowsCtrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  ctrlKey?: boolean;
  dblClickPassthrough?: boolean;
  /**
   * When set, Shift must be exactly this (true = held, false = not held) and the key matches case-insensitively
   * (Shift turns `k` into `K`). Lets ⌘K and ⌘⇧K be two different shortcuts.
   */
  exactShift?: boolean;
  /** Fire even while focus is in an input, textarea or contenteditable — for global palettes. */
  allowInInputs?: boolean;
}

export const useKey = (
  key: string,
  onKeyPress: (e: KeyboardEvent) => void,
  options: Options = {},
) => {
  const lastPressRef = React.useRef<number | null>(null);
  const { metaKey, shiftKey, ctrlKey, metaWindowsCtrlKey, dblClickPassthrough, exactShift, allowInInputs } = options;

  React.useEffect(() => {
    const keyPressFn = (e: KeyboardEvent) => {
      const now = Date.now();
      const diff = now - (lastPressRef.current ?? 0);
      lastPressRef.current = now;

      if (!allowInInputs && e.target instanceof HTMLElement && !(dblClickPassthrough && diff < 250)) {
        if (e.target.tagName === 'TEXTAREA') return;
        if (e.target.tagName === 'INPUT') return;
        if (e.target.contentEditable === 'true') return;
      }

      if (exactShift != null && e.shiftKey !== exactShift) return;
      const keyMatches = exactShift != null ? e.key.toLowerCase() === key.toLowerCase() : e.key === key;
      if (keyMatches) {
        const extraKey =
          metaKey != null || shiftKey != null || ctrlKey != null || metaWindowsCtrlKey != null;

        if (extraKey === true) {
          if (metaKey != null && metaKey == e.metaKey) {
            onKeyPress(e);
          } else if (ctrlKey != null && ctrlKey === e.ctrlKey) {
            onKeyPress(e);
          } else if (shiftKey != null && shiftKey === e.shiftKey) {
            onKeyPress(e);
          } else if (metaWindowsCtrlKey != null) {
            const isMac = isMacLike();
            if (isMac && e.metaKey) {
              onKeyPress(e);
            } else if (!isMac && e.ctrlKey) {
              onKeyPress(e);
            }
          }
        } else {
          onKeyPress(e);
        }
      }
    };

    window.addEventListener('keydown', keyPressFn);
    return () => {
      window.removeEventListener('keydown', keyPressFn);
    };
  }, [onKeyPress, key, metaKey, shiftKey, ctrlKey, metaWindowsCtrlKey, dblClickPassthrough, exactShift, allowInInputs]);
};
